import {
  collection,
  endAt,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  startAt,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { flattenBarcodeInstance } from "./barcodeTrackingHelper";

export const BARCODE_PARENT_COLLECTION = "factorybarcode";
export const BARCODE_INSTANCE_SUBCOLLECTION = "instances";
export const BARCODE_PARENT_PAGE_SIZE = 25;
export const BARCODE_INSTANCE_LIMIT = 250;

function firstServerSearch(filters) {
  const candidates = [
    ["BarcodeNumber", filters.barcodeNumber],
    ["SerialNumber", filters.serialNumber],
    ["ProductCode", filters.productCode],
  ];
  return candidates.find(([, value]) => String(value || "").trim()) || null;
}

function buildParentQuery(filters, cursor) {
  const parentCollection = collection(db, BARCODE_PARENT_COLLECTION);
  const serverSearch = firstServerSearch(filters);

  if (!serverSearch) {
    const constraints = [orderBy("BarcodeNumber")];
    if (cursor) constraints.push(startAfter(cursor));
    constraints.push(limit(BARCODE_PARENT_PAGE_SIZE));
    return query(parentCollection, ...constraints);
  }

  const [field, rawTerm] = serverSearch;
  const term = String(rawTerm).trim();
  const constraints = [orderBy(field)];
  if (cursor) {
    constraints.push(startAfter(cursor));
  } else {
    constraints.push(startAt(term));
  }
  constraints.push(
    endAt(`${term}\uf8ff`),
    limit(BARCODE_PARENT_PAGE_SIZE)
  );
  return query(parentCollection, ...constraints);
}

async function loadInstances(parentDocument) {
  const instanceQuery = query(
    collection(
      db,
      BARCODE_PARENT_COLLECTION,
      parentDocument.id,
      BARCODE_INSTANCE_SUBCOLLECTION
    ),
    limit(BARCODE_INSTANCE_LIMIT + 1)
  );
  const snapshot = await getDocs(instanceQuery);
  return {
    documents: snapshot.docs.slice(0, BARCODE_INSTANCE_LIMIT),
    truncated: snapshot.docs.length > BARCODE_INSTANCE_LIMIT,
  };
}

export async function fetchBarcodeTrackingPage({
  filters = {},
  cursor = null,
} = {}) {
  const parentSnapshot = await getDocs(buildParentQuery(filters, cursor));
  const loadedParents = await Promise.all(
    parentSnapshot.docs.map(async (parentDocument) => ({
      parentDocument,
      instances: await loadInstances(parentDocument),
    }))
  );

  const rows = [];
  const truncatedParentIds = [];

  for (const loaded of loadedParents) {
    if (loaded.instances.truncated) {
      truncatedParentIds.push(loaded.parentDocument.id);
    }
    for (const instanceDocument of loaded.instances.documents) {
      rows.push(
        flattenBarcodeInstance(
          loaded.parentDocument,
          instanceDocument
        )
      );
    }
  }

  return {
    rows,
    cursor:
      parentSnapshot.docs[parentSnapshot.docs.length - 1] || null,
    hasMore: parentSnapshot.docs.length === BARCODE_PARENT_PAGE_SIZE,
    parentCount: parentSnapshot.docs.length,
    truncatedParentIds,
    serverSearchField: firstServerSearch(filters)?.[0] || "BarcodeNumber",
  };
}
